import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarClock, Loader2, Plus, RefreshCw } from 'lucide-react';
import { salesAPI } from '../../services/salesApi';
import { athensYmd } from '../../utils/athensDate';
import {
  Column, DataTable, EmptyState, FilterSelect, GHOST_BTN, ListPage, ListToolbar, Notice, PRIMARY_BTN, Stat, Stats,
  StatusChip, matchesSearch,
} from '../shared/listPage';
import LeadPanel from './LeadPanel';
import { MoveDialog, SalesBoard, moveNeedsInput } from './SalesBoard';
import SalesMap from './SalesMap';
import SalesReports from './SalesReports';
import {
  ACTIVE_STATUSES, KIND_OPTIONS, Lead, LeadInput, LeadStatus, SOURCE_OPTIONS, STATUS_OPTIONS, SalesMeta, kindLabel,
  leadValue, relativeDay, sourceLabel, statusLabel, statusTone, todayBuckets,
} from './salesModel';

/**
 * Πωλήσεις — οι σελίδες του πλαϊνού μενού, ίδιες για τον super admin και τον ρόλο 'sales':
 *  - «Σήμερα»: ποιοι καθυστέρησαν, ποιους παίρνω σήμερα, νέοι χωρίς επαφή (οι 10 με τον υψηλότερο βαθμό), ποιους
 *    ξαναδοκιμάζουμε, οι επόμενες 7 ημέρες — ο καθένας σε πίνακα όπως οι υπόλοιπες σελίδες της διαχείρισης.
 *  - «Υποψήφιοι πελάτες»: όλοι, με φίλτρα και αναζήτηση χωρίς τόνους (στη μνήμη — λίγες εκατοντάδες).
 *  - «Στάδια»: στήλες ανά στάδιο, μετακίνηση με σύρσιμο (SalesBoard).
 *  - «Χάρτης»: οι υποψήφιοι στον χάρτη (SalesMap).
 * Χωρίς ανάθεση/υπεύθυνο στις οθόνες (απόφαση χρήστη 3/11/2026: «ένα άτομο θα αναλάβει όλο αυτό») — το owner_id
 * γράφεται ακόμη από τον server (ποιος τον έφτιαξε) αλλά δεν φαίνεται.
 * Κλειστός διακόπτης: ο super admin τον ανοίγει από εδώ· οι υπόλοιποι βλέπουν μόνο ότι είναι κλειστός.
 */

export type SalesView = 'today' | 'list' | 'board' | 'map' | 'reports';

const TITLES: Record<SalesView, string> = {
  today: 'Σήμερα', list: 'Υποψήφιοι πελάτες', board: 'Στάδια', map: 'Χάρτης', reports: 'Αναφορές',
};
const UNTOUCHED_SHOWN = 10;
const longDate = (ymd: string) => new Date(`${ymd}T12:00:00Z`).toLocaleDateString('el-GR', {
  timeZone: 'Europe/Athens', weekday: 'long', day: 'numeric', month: 'long',
});

interface Props {
  view: SalesView;
  /** «Σήμερα» → «Υποψήφιοι» (π.χ. «και άλλοι 58»). Το αποφασίζει το πλαίσιο, που κρατά το μενού. */
  onShowList?: () => void;
  /** «Έγινε πελάτης» → «Νέο κατάστημα» (μόνο ο super admin: ο ρόλος sales δεν ανοίγει καταστήματα). */
  onCreateStore?: (lead: Lead) => void;
}

const SalesWorkspace: React.FC<Props> = ({ view, onShowList, onCreateStore }) => {
  const [status, setStatus] = useState<{ enabled: boolean; is_super: boolean } | null>(null);
  const [statusFailed, setStatusFailed] = useState(false);
  const [meta, setMeta] = useState<SalesMeta | null>(null);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [kindFilter, setKindFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [selected, setSelected] = useState<number | 'new' | null>(null);
  const [move, setMove] = useState<{ lead: Lead; to: LeadStatus } | null>(null);
  const [moveError, setMoveError] = useState<string | null>(null);
  const [toggling, setToggling] = useState(false);
  const today = athensYmd();

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const [m, d] = await Promise.all([salesAPI.getMeta(), salesAPI.getLeads()]);
      setMeta(m);
      setLeads(d.leads || []);
      setLoadError(null);
    } catch (e: any) {
      setLoadError(e?.response?.data?.message || e?.response?.data?.error || 'Οι υποψήφιοι δεν φόρτωσαν');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    salesAPI.getStatus().then(setStatus).catch(() => setStatusFailed(true));
  }, []);
  useEffect(() => { if (status?.enabled) loadLeads(); }, [status?.enabled, loadLeads]);

  const toggle = async (enabled: boolean) => {
    const msg = enabled
      ? 'Άνοιγμα των πωλήσεων σε αυτόν τον server;\n\nΜόνο στον server της xentrix — ποτέ στον server ενός πελάτη (Κουλουράδες).'
      : 'Κλείσιμο των πωλήσεων σε αυτόν τον server; Οι υποψήφιοι μένουν στη βάση· οι φόρμες του site σταματούν να γράφουν.';
    if (!window.confirm(msg)) return;
    setToggling(true);
    try { setStatus(await salesAPI.setStatus(enabled)); } catch { window.alert('Η αλλαγή απέτυχε'); } finally { setToggling(false); }
  };

  const buckets = useMemo(() => todayBuckets(leads, today), [leads, today]);
  const untouched = useMemo(() => [...buckets.untouched]
    .sort((a, b) => (b.fit_score ?? 0) - (a.fit_score ?? 0) || a.name.localeCompare(b.name, 'el')), [buckets.untouched]);

  const rows = useMemo(() => leads.filter(l => {
    if (statusFilter === 'active' && !ACTIVE_STATUSES.includes(l.status)) return false;
    if (statusFilter && statusFilter !== 'active' && statusFilter !== 'all' && l.status !== statusFilter) return false;
    if (kindFilter && l.kind !== kindFilter) return false;
    if (sourceFilter && l.source !== sourceFilter) return false;
    return matchesSearch(search, l.name, l.area, l.phone, l.email, l.contact_name, l.address);
  }), [leads, statusFilter, kindFilter, sourceFilter, search]);

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const l of leads) c[l.status] = (c[l.status] || 0) + 1;
    return c;
  }, [leads]);

  const replaceLead = useCallback((lead: Lead) => {
    setLeads(prev => (prev.some(l => l.id === lead.id) ? prev.map(l => (l.id === lead.id ? lead : l)) : [lead, ...prev]));
  }, []);
  const onSaved = (lead: Lead) => {
    replaceLead(lead);
    setSelected(cur => (cur === 'new' ? lead.id : cur));
  };
  const onGeo = useCallback((id: number, geo: Pick<Lead, 'lat' | 'lng' | 'geo_precision'>) => {
    setLeads(prev => prev.map(l => (l.id === id ? { ...l, ...geo } : l)));
  }, []);

  // «Στάδια»: μετακίνηση. Ό,τι δεν χρειάζεται ερώτηση γράφεται αμέσως.
  const doMove = async (lead: Lead, changes: LeadInput) => {
    const r = await salesAPI.updateLead(lead.id, changes);
    replaceLead(r.lead);
    setMove(null);
  };
  const onMove = (lead: Lead, to: LeadStatus) => {
    setMoveError(null);
    if (moveNeedsInput(lead, to)) { setMove({ lead, to }); return; }
    doMove(lead, { status: to }).catch((e: any) => setMoveError(e?.response?.data?.error || 'Η μετακίνηση απέτυχε'));
  };

  // ── Στήλες ────────────────────────────────────────────────────────────────
  const nameCol = useMemo<Column<Lead>>(() => ({
    key: 'name', header: 'Επωνυμία', width: 'minmax(200px, 2fr)', stack: 'title', sortable: true,
    render: l => (
      <>
        <span className="block font-medium text-t1">{l.name}</span>
        <span className="block text-xs text-t3 truncate">
          {[kindLabel(l.kind), l.area, l.track === 'chain' ? `αλυσίδα${l.stores_count ? ` · ${l.stores_count}` : ''}` : null]
            .filter(Boolean).join(' · ')}
        </span>
      </>
    ),
    stackRender: l => l.name,
  }), []);
  const statusCol = useMemo<Column<Lead>>(() => ({
    key: 'status', header: 'Στάδιο', width: '150px', sortable: true,
    sortValue: l => STATUS_OPTIONS.findIndex(o => o.value === l.status),
    render: l => <StatusChip tone={statusTone(l.status)}>{statusLabel(l.status)}</StatusChip>,
    stackRender: l => statusLabel(l.status),
  }), []);
  const nextCol = useMemo<Column<Lead>>(() => ({
    key: 'next_action_at', header: 'Επόμενο βήμα', width: 'minmax(170px, 1.5fr)', sortable: true,
    sortValue: l => (ACTIVE_STATUSES.includes(l.status) ? l.next_action_at : null),
    render: l => (ACTIVE_STATUSES.includes(l.status) && l.next_action_at ? (
      <>
        <span className={`block tabular-nums ${l.next_action_at < today ? 'text-red-700 dark:text-red-400 font-medium' : 'text-t1'}`}>
          {relativeDay(l.next_action_at, today)}
        </span>
        {l.next_action && <span className="block text-xs text-t3 truncate">{l.next_action}</span>}
      </>
    ) : <span className="text-t4">—</span>),
    stackRender: l => (l.next_action_at && ACTIVE_STATUSES.includes(l.status) ? relativeDay(l.next_action_at, today) : ''),
  }), [today]);
  const phoneCol = useMemo<Column<Lead>>(() => ({
    key: 'phone', header: 'Τηλέφωνο', width: 'minmax(130px, 1fr)', hideBelow: 'narrow',
    render: l => (l.phone ? <span className="tabular-nums text-t2">{l.phone}</span> : <span className="text-t4">—</span>),
  }), []);
  const sourceCol = useMemo<Column<Lead>>(() => ({
    key: 'source', header: 'Πηγή', width: 'minmax(120px, 1fr)', hideBelow: 'medium',
    render: l => <span className="text-t2">{sourceLabel(l.source)}</span>,
  }), []);
  const scoreCol = useMemo<Column<Lead>>(() => ({
    key: 'fit_score', header: 'Βαθμός', width: '108px', align: 'right', hideBelow: 'narrow', sortable: true,
    render: l => <span className="tabular-nums text-t2">{l.fit_score ?? '—'}</span>,
  }), []);
  const retryCol = useMemo<Column<Lead>>(() => ({
    key: 'retry_at', header: 'Ξαναδοκίμασε', width: 'minmax(140px, 1fr)',
    render: l => <span className="text-t2">{l.retry_at ? relativeDay(l.retry_at, today) : '—'}</span>,
  }), [today]);

  const valueCol = useMemo<Column<Lead>>(() => ({
    key: 'expected_monthly', header: 'Αξία', width: '120px', align: 'right', hideBelow: 'medium', sortable: true,
    sortValue: l => (leadValue(l) || null),
    render: l => (leadValue(l) ? <span className="tabular-nums text-t2">{Math.round(leadValue(l)).toLocaleString('el-GR')} €</span>
      : <span className="text-t4">—</span>),
  }), []);
  const listColumns = useMemo(() => [nameCol, statusCol, nextCol, phoneCol, sourceCol, valueCol, scoreCol],
    [nameCol, statusCol, nextCol, phoneCol, sourceCol, valueCol, scoreCol]);
  const dueColumns = useMemo(() => [nameCol, nextCol, phoneCol, statusCol], [nameCol, nextCol, phoneCol, statusCol]);
  const newColumns = useMemo(() => [nameCol, sourceCol, phoneCol, scoreCol], [nameCol, sourceCol, phoneCol, scoreCol]);
  const retryColumns = useMemo(() => [nameCol, statusCol, retryCol, phoneCol], [nameCol, statusCol, retryCol, phoneCol]);

  const title = TITLES[view];

  if (statusFailed) {
    return <ListPage eyebrow="Πωλήσεις" title={title}><Notice tone="danger" testId="sales-status-failed">Οι πωλήσεις δεν φόρτωσαν.</Notice></ListPage>;
  }
  if (!status) {
    return (
      <ListPage eyebrow="Πωλήσεις" title={title}>
        <div role="status" className="flex items-center gap-2 py-10 justify-center text-sm text-t3">
          <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Φόρτωση…
        </div>
      </ListPage>
    );
  }
  if (!status.enabled) {
    return (
      <ListPage eyebrow="Πωλήσεις" title={title}>
        <div className="space-y-4" data-testid="sales-disabled">
          <Notice>Οι πωλήσεις είναι κλειστές σε αυτόν τον server.</Notice>
          {status.is_super && (
            <button type="button" className={PRIMARY_BTN} disabled={toggling} onClick={() => toggle(true)} data-testid="sales-enable">
              {toggling && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />} Άνοιγμα πωλήσεων
            </button>
          )}
        </div>
      </ListPage>
    );
  }

  const actions = (
    <>
      <button type="button" className={GHOST_BTN} onClick={loadLeads} disabled={loading} title="Ανανέωση" aria-label="Ανανέωση">
        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
      </button>
      <button type="button" className={PRIMARY_BTN} onClick={() => setSelected('new')} data-testid="sales-new">
        <Plus className="w-4 h-4" aria-hidden="true" /> Νέος υποψήφιος
      </button>
    </>
  );

  const panels = (
    <>
      <LeadPanel
        open={selected !== null}
        leadId={typeof selected === 'number' ? selected : null}
        meta={meta}
        allLeads={leads}
        isSuper={status.is_super}
        today={today}
        onClose={() => setSelected(null)}
        onSaved={onSaved}
        onOpenLead={id => setSelected(id)}
        onCreateStore={onCreateStore}
        onDeleted={id => { setLeads(prev => prev.filter(l => l.id !== id)); setSelected(null); }}
      />
      <MoveDialog move={move} today={today} onCancel={() => setMove(null)} onConfirm={doMove} />
    </>
  );

  // Μία ενότητα του «Σήμερα»: τίτλος + πίνακας (μόνο αν έχει κάτι).
  const section = (id: string, label: string, list: Lead[], columns: Column<Lead>[], footer?: React.ReactNode) => (list.length > 0 && (
    <section className="space-y-2" aria-labelledby={`sales-sec-${id}`} data-testid={`sales-sec-${id}`}>
      <h2 id={`sales-sec-${id}`} className="text-sm font-semibold text-t1">
        {label} <span className="text-t3 font-normal">· {id === 'untouched' ? buckets.untouched.length : list.length}</span>
      </h2>
      <DataTable columns={columns} rows={list} rowKey={l => l.id} onRowClick={l => setSelected(l.id)}
        selectedKey={typeof selected === 'number' ? selected : null} empty={null} rowLabel={l => l.name} footer={footer} dense />
    </section>
  ));
  const loadingBlock = (
    <div role="status" className="flex items-center gap-2 py-10 justify-center text-sm text-t3">
      <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Φόρτωση…
    </div>
  );

  if (view === 'today') {
    const nothing = buckets.overdue.length + buckets.today.length + buckets.untouched.length + buckets.retry.length
      + buckets.upcoming.length === 0;
    const more = buckets.untouched.length - UNTOUCHED_SHOWN;
    return (
      <ListPage eyebrow="Πωλήσεις" title="Σήμερα" subtitle={longDate(today)} actions={actions}>
        {loadError && <Notice tone="danger" testId="sales-load-error">{loadError}</Notice>}
        <Stats testId="sales-today-counts">
          <Stat label="Καθυστέρησαν" value={buckets.overdue.length} />
          <Stat label="Σήμερα" value={buckets.today.length} />
          <Stat label="Νέοι χωρίς επαφή" value={buckets.untouched.length} />
          <Stat label="Να ξαναδοκιμάσουμε" value={buckets.retry.length} />
        </Stats>
        <div className="mt-4 space-y-8" data-testid="sales-today">
          {loading && leads.length === 0 ? loadingBlock : nothing ? (
            <EmptyState icon={CalendarClock} title="Τίποτα για σήμερα" hint="Δεν υπάρχει επόμενο βήμα που να έφτασε ή να πέρασε." />
          ) : (
            <>
              {section('overdue', 'Καθυστέρησαν', buckets.overdue, dueColumns)}
              {section('today', 'Σήμερα', buckets.today, dueColumns)}
              {section('untouched', 'Νέοι χωρίς επαφή', untouched.slice(0, UNTOUCHED_SHOWN), newColumns, more > 0 ? (
                <div className="px-4 py-2.5 text-sm text-t3">
                  Οι {UNTOUCHED_SHOWN} με τον υψηλότερο βαθμό · άλλοι {more}
                  {onShowList && (
                    <> — <button type="button" className="text-brand font-medium hover:underline" onClick={onShowList}>δες τους στους Υποψήφιους</button></>
                  )}
                </div>
              ) : undefined)}
              {section('retry', 'Να ξαναδοκιμάσουμε', buckets.retry, retryColumns)}
              {section('upcoming', 'Τις επόμενες 7 ημέρες', buckets.upcoming, dueColumns)}
            </>
          )}
        </div>
        {panels}
      </ListPage>
    );
  }

  if (view === 'board') {
    return (
      <ListPage eyebrow="Πωλήσεις" title="Στάδια" subtitle="Σύρε μια κάρτα σε άλλη στήλη για να αλλάξει στάδιο." actions={actions}>
        {loadError && <Notice tone="danger" testId="sales-load-error">{loadError}</Notice>}
        {moveError && <Notice tone="danger" testId="sales-move-failed">{moveError}</Notice>}
        {loading && leads.length === 0 ? loadingBlock
          : <SalesBoard leads={leads} today={today} onOpen={id => setSelected(id)} onMove={onMove} />}
        {panels}
      </ListPage>
    );
  }

  if (view === 'reports') {
    return (
      <ListPage eyebrow="Πωλήσεις" title="Αναφορές" actions={actions}>
        <SalesReports onOpen={id => setSelected(id)} />
        {panels}
      </ListPage>
    );
  }

  if (view === 'map') {
    return (
      <ListPage eyebrow="Πωλήσεις" title="Χάρτης" actions={actions}>
        {loadError && <Notice tone="danger" testId="sales-load-error">{loadError}</Notice>}
        <SalesMap leads={leads} today={today} onOpen={id => setSelected(id)} onGeo={onGeo} />
        {panels}
      </ListPage>
    );
  }

  return (
    <ListPage eyebrow="Πωλήσεις" title="Υποψήφιοι πελάτες" actions={actions}>
      {loadError && <Notice tone="danger" testId="sales-load-error">{loadError}</Notice>}
      <ListToolbar
        search={{ value: search, onChange: setSearch, placeholder: 'Επωνυμία, περιοχή, τηλέφωνο…' }}
        filters={(
          <>
            <FilterSelect label="Στάδιο" value={statusFilter} onChange={setStatusFilter}
              options={[{ value: 'active', label: 'Ενεργοί' }, { value: 'all', label: 'Όλοι' }, ...STATUS_OPTIONS]} />
            <FilterSelect label="Είδος" value={kindFilter} onChange={setKindFilter} options={KIND_OPTIONS} allLabel="Όλα τα είδη" />
            <FilterSelect label="Πηγή" value={sourceFilter} onChange={setSourceFilter} options={SOURCE_OPTIONS} allLabel="Όλες οι πηγές" />
          </>
        )}
        summary={(
          <Stats testId="sales-counts">
            <Stat label="Εμφανίζονται" value={`${rows.length} από ${leads.length}`} />
            {STATUS_OPTIONS.filter(o => counts[o.value]).map(o => <Stat key={o.value} label={o.label} value={counts[o.value]} />)}
          </Stats>
        )}
      />
      <DataTable
        columns={listColumns}
        rows={rows}
        rowKey={l => l.id}
        onRowClick={l => setSelected(l.id)}
        selectedKey={typeof selected === 'number' ? selected : null}
        loading={loading}
        defaultSort={{ key: 'next_action_at', dir: 'asc' }}
        empty={<EmptyState title="Κανένας υποψήφιος" hint="Άλλαξε τα φίλτρα ή πρόσθεσε νέο υποψήφιο." />}
        rowLabel={l => l.name}
        testId="sales-table"
      />
      {status.is_super && (
        <p className="mt-6 text-xs text-t3">
          Οι πωλήσεις είναι ανοιχτές σε αυτόν τον server.{' '}
          <button type="button" className="underline hover:text-t1" disabled={toggling} onClick={() => toggle(false)} data-testid="sales-disable">
            Κλείσιμο
          </button>
        </p>
      )}
      {panels}
    </ListPage>
  );
};

export default SalesWorkspace;
